/**
 * Chunked / resumable upload client for large admin media files.
 *
 * Direct multipart POST /api/admin/upload/{audio,video} hits Cloudflare's
 * ~100MB body-size cap on production. This helper transparently switches
 * to the chunked flow when the file is larger than SMALL_UPLOAD_LIMIT.
 *
 * Usage:
 *   const asset = await uploadMedia({
 *     file,                       // File object
 *     kind: 'audio' | 'video',
 *     apiUrl: REACT_APP_BACKEND_URL,
 *     token: JWT,
 *     onProgress: ({ loaded, total, phase }) => ...
 *   });
 *   // asset === { id, url, storagePath, size, sha256, deduped }
 *
 * Resume-after-drop:
 *   - Sessions in progress are keyed by (fileName, fileSize, lastModified, kind)
 *     in localStorage. Re-calling uploadMedia() with the same File auto-checks
 *     the server for `receivedIndices` and skips those.
 *   - If the server says the session is 410 (files vanished after redeploy),
 *     we abandon the stored sessionId and start a fresh one automatically.
 */

const LS_KEY = "rbr_chunked_sessions_v1";
export const SMALL_UPLOAD_LIMIT = 90 * 1024 * 1024;      // <=90MB → direct POST
const DEFAULT_CHUNK_SIZE = 15 * 1024 * 1024;             // 15MB
const MAX_CHUNK_RETRIES = 3;

function _loadSessions() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    return {};
  }
}

function _saveSessions(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {
    /* quota — ignore */
  }
}

function _sessionKey({ file, kind }) {
  return `${kind}::${file.name}::${file.size}::${file.lastModified || 0}`;
}

function _rememberSession(key, sessionId) {
  const all = _loadSessions();
  all[key] = { sessionId, ts: Date.now() };
  _saveSessions(all);
}

function _forgetSession(key) {
  const all = _loadSessions();
  delete all[key];
  _saveSessions(all);
}

function _getSessionId(key) {
  const all = _loadSessions();
  const entry = all[key];
  if (!entry) return null;
  // Purge entries older than 24h — sessions have 2h TTL, so anything older
  // than a day is definitely gone.
  if (Date.now() - (entry.ts || 0) > 24 * 60 * 60 * 1000) {
    _forgetSession(key);
    return null;
  }
  return entry.sessionId || null;
}

async function _directUpload({ file, kind, apiUrl, token, onProgress }) {
  // Backward-compatible path for small files. Uses the existing endpoint.
  const endpoint =
    kind === "video" ? "/api/admin/upload/video" :
    kind === "audio" ? "/api/admin/upload/audio" :
    kind === "image" ? "/api/admin/upload/image" :
    null;
  if (!endpoint) throw new Error(`Unsupported kind: ${kind}`);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${apiUrl}${endpoint}`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress({ loaded: e.loaded, total: e.total, phase: "upload" });
      }
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || "{}");
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data.detail || `Upload failed (${xhr.status})`));
      } catch (e) {
        reject(new Error(`Upload response parse error: ${e.message}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  });
}

async function _initSession({ file, kind, apiUrl, token }) {
  const r = await fetch(`${apiUrl}/api/admin/upload/chunked/init`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      filename: file.name,
      size: file.size,
      kind,
      contentType: file.type || undefined,
    }),
  });
  const data = await r.json();
  if (!r.ok) {
    const msg = typeof data.detail === "string" ? data.detail : (data.detail?.message || `Init failed (${r.status})`);
    throw new Error(msg);
  }
  return data;   // { sessionId, chunkSize, chunkSizeMax, totalChunks, expiresAt }
}

async function _statusSession({ sessionId, apiUrl, token }) {
  const r = await fetch(`${apiUrl}/api/admin/upload/chunked/${sessionId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.status === 404 || r.status === 410 || r.status === 403) {
    return { canResume: false, status: r.status };
  }
  const data = await r.json();
  if (!r.ok) return { canResume: false, status: r.status };
  return data;
}

async function _putChunk({ sessionId, index, blob, apiUrl, token, signal }) {
  let attempt = 0;
  while (true) {
    attempt += 1;
    try {
      const r = await fetch(`${apiUrl}/api/admin/upload/chunked/${sessionId}/chunk/${index}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/octet-stream",
        },
        body: blob,
        signal,
      });
      if (r.status === 410) {
        const data = await r.json().catch(() => ({}));
        const err = new Error(data.detail?.message || "Session expired");
        err.code = data.detail?.code || "session_expired";
        err.terminal = true;
        throw err;
      }
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `chunk ${index} failed (${r.status})`);
      }
      return await r.json();
    } catch (e) {
      if (e.terminal || attempt >= MAX_CHUNK_RETRIES) throw e;
      // Exponential backoff: 500ms, 1500ms, 3500ms
      await new Promise(res => setTimeout(res, 500 * (2 ** (attempt - 1))));
    }
  }
}

async function _completeSession({ sessionId, apiUrl, token }) {
  // Server finalizes in the background for large files: 202 → poll until done.
  for (let attempt = 0; attempt < 400; attempt += 1) {
    const r = await fetch(`${apiUrl}/api/admin/upload/chunked/${sessionId}/complete`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await r.json().catch(() => ({}));
    if (r.ok && r.status !== 202) return data;
    if (r.status !== 202 && r.status !== 409) {
      const msg = typeof data.detail === "string" ? data.detail : (data.detail?.message || data.detail?.code || `Complete failed (${r.status})`);
      throw new Error(msg);
    }
    // Wait for the background assembly to finish, then re-POST (idempotent).
    for (;;) {
      await new Promise((res) => setTimeout(res, 3000));
      const s = await fetch(`${apiUrl}/api/admin/upload/chunked/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
      const st = await s.json().catch(() => ({}));
      if (!s.ok) throw new Error(st.detail?.message || st.detail || `Status check failed (${s.status})`);
      if (st.status === "complete") break;
      if (st.status === "aborted") throw new Error("Upload failed while finalizing — please retry.");
    }
  }
  throw new Error("Upload finalization timed out");
}

export async function abortChunkedSession({ file, kind, apiUrl, token }) {
  const key = _sessionKey({ file, kind });
  const sessionId = _getSessionId(key);
  if (!sessionId) return;
  try {
    await fetch(`${apiUrl}/api/admin/upload/chunked/${sessionId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    /* ignore */
  }
  _forgetSession(key);
}

export async function uploadMedia({ file, kind, apiUrl, token, onProgress, signal }) {
  if (!file) throw new Error("No file");
  if (!["audio", "video"].includes(kind)) throw new Error(`Unsupported kind: ${kind}`);
  if (!token) throw new Error("Not authenticated");

  // Small file — take the existing direct path.
  if (file.size <= SMALL_UPLOAD_LIMIT) {
    return _directUpload({ file, kind, apiUrl, token, onProgress });
  }

  const key = _sessionKey({ file, kind });
  let sessionId = _getSessionId(key);
  let received = new Set();
  let chunkSize = DEFAULT_CHUNK_SIZE;
  let totalChunks = 0;

  // Try to resume a stored session first.
  if (sessionId) {
    const status = await _statusSession({ sessionId, apiUrl, token });
    if (status.canResume && status.status !== "complete") {
      chunkSize = status.chunkSize || DEFAULT_CHUNK_SIZE;
      totalChunks = status.totalChunks || Math.ceil(file.size / chunkSize);
      received = new Set(status.received || []);
    } else {
      _forgetSession(key);
      sessionId = null;
    }
  }

  if (!sessionId) {
    const init = await _initSession({ file, kind, apiUrl, token });
    sessionId = init.sessionId;
    chunkSize = init.chunkSize || DEFAULT_CHUNK_SIZE;
    totalChunks = init.totalChunks;
    _rememberSession(key, sessionId);
  }

  // Upload missing chunks sequentially (keeps memory bounded).
  let uploadedBytes = received.size * chunkSize;
  const emitProgress = () => {
    if (onProgress) {
      onProgress({
        loaded: Math.min(uploadedBytes, file.size),
        total: file.size,
        phase: "upload",
        sessionId,
      });
    }
  };
  emitProgress();

  for (let i = 0; i < totalChunks; i++) {
    if (signal?.aborted) throw new Error("Aborted");
    if (received.has(i)) continue;
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const blob = file.slice(start, end);
    try {
      await _putChunk({ sessionId, index: i, blob, apiUrl, token, signal });
    } catch (e) {
      if (e.terminal) {
        // Session gone on server — restart from scratch.
        _forgetSession(key);
        throw e;
      }
      throw e;
    }
    received.add(i);
    uploadedBytes += (end - start);
    emitProgress();
  }

  if (onProgress) {
    onProgress({ loaded: file.size, total: file.size, phase: "assembling", sessionId });
  }

  const asset = await _completeSession({ sessionId, apiUrl, token });
  _forgetSession(key);

  if (onProgress) {
    onProgress({ loaded: file.size, total: file.size, phase: "complete", sessionId });
  }
  return asset;
}
